import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { productsUrl, warehouseProductUrl } from '@/utils/productLinks';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { countryTaxRateFor } from '@/utils/countries';
import { FoodOut, FoodCreate, FoodTypeOut, EventOut, EventVenueOut, BulkAssignVenueRequest, ProductOut, BulkImportResult as BulkImportResultData } from '@/types/api';
import { getProductImageProps } from '@/utils/imageUtils';
import { canAccessAllEvents, isSuperAdmin, isOrganizer } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import CartaProductPicker from '@/components/molecules/CartaProductPicker/CartaProductPicker';
import BulkImportResult from '@/components/molecules/BulkImportResult/BulkImportResult';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import Pagination from '@/components/molecules/Pagination/Pagination';
import { TranslationFields, TranslationFieldsHandle } from '@/components/molecules/TranslationFields';
import './FoodManagement.scss';
import { useCurrency } from '@/contexts/CurrencyContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';

interface FoodFormData {
  name: string;
  price: number;
  tax_rate: number;
  image_url: string;
  description: string;
  allergens: string[];
  event_id: number;
  event_venue_id: number | null;
  product_id: number | null;
  type_id: number;
  stock_quantity: number;
  max_stock: number;
  min_stock_threshold: number;
  is_available: boolean;
}

const initialFormData: FoodFormData = {
  name: '',
  price: 0,
  tax_rate: 0,
  image_url: '',
  description: '',
  allergens: [],
  event_id: 0,
  event_venue_id: null,
  product_id: null,
  type_id: 0,
  stock_quantity: 0,
  max_stock: 100,
  min_stock_threshold: 10,
  is_available: true
};

// Carta rows per page in the table
const PAGE_SIZE = 25;

const FoodManagement: React.FC = () => {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { format } = usePriceFormat();
  const { user } = useAuth();
  const [foods, setFoods] = useState<FoodOut[]>([]);
  const [foodTypes, setFoodTypes] = useState<FoodTypeOut[]>([]);
  const [events, setEvents] = useState<EventOut[]>([]);
  const [venues, setVenues] = useState<Record<number, EventVenueOut[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFood, setEditingFood] = useState<FoodOut | null>(null);
  const translationsRef = useRef<TranslationFieldsHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FoodFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportEventId, setBulkImportEventId] = useState<number | null>(null);
  const [priceInput, setPriceInput] = useState<string>('');
  const [taxInput, setTaxInput] = useState<string>('');
  const [isTaxIncluded, setIsTaxIncluded] = useState<boolean>(false);
  const [prevTaxInput, setPrevTaxInput] = useState<string>('');
  const [countryTaxRates, setCountryTaxRates] = useState<Record<string, number>>({});

  // Bulk assign (venue / event) selection state
  const [selectedFoodIds, setSelectedFoodIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [bulkTargetEventId, setBulkTargetEventId] = useState<number | null>(null);
  const [bulkTargetVenueId, setBulkTargetVenueId] = useState<number | null>(null);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [localProducts, setLocalProducts] = useState<ProductOut[]>([]);
  const [localProductsLoaded, setLocalProductsLoaded] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResultData | null>(null);
  const navigate = useNavigate();
  const [showCartaExplainer, setShowCartaExplainer] = useState(() => {
    try {
      return localStorage.getItem('carta-explainer-dismissed') !== '1';
    } catch {
      return true;
    }
  });

  const dismissCartaExplainer = () => {
    setShowCartaExplainer(false);
    try {
      localStorage.setItem('carta-explainer-dismissed', '1');
    } catch {
      // ignore - per-viewer convenience only
    }
  };

  // Catalog products of the selected event's Local: every carta row is
  // created from one of them (empty if the event has no Local).
  useEffect(() => {
    const event = events.find((e) => e.id === formData.event_id);
    if (!event?.local_id) {
      setLocalProducts([]);
      setLocalProductsLoaded(true);
      return;
    }
    setLocalProductsLoaded(false);
    apiService.getProducts(event.local_id)
      .then((data) => {
        const products = data.filter((p) => !p.is_drink);
        setLocalProducts(products);
        // A product of another Local can't stay selected after changing event
        setFormData((prev) => (prev.product_id && !products.some((p) => p.id === prev.product_id)
          ? { ...prev, product_id: null }
          : prev));
      })
      .catch(() => setLocalProducts([]))
      .finally(() => setLocalProductsLoaded(true));
  }, [formData.event_id, events]);

  // Fetch venues for a specific event
  const fetchVenues = useCallback(async (eventId: number) => {
    try {
      const venuesData = await apiService.getEventVenues(eventId);
      setVenues(prev => ({ ...prev, [eventId]: venuesData }));
    } catch (err: any) {
      console.error(`Error fetching venues for event ${eventId}:`, err);
    }
  }, []);

  // Fetch country tax rates
  const fetchCountryTaxRates = useCallback(async () => {
    try {
      const response = await apiService.getAllCountryTaxRates();
      setCountryTaxRates(response.country_tax_rates || {});
    } catch (err: any) {
      console.error('Error fetching country tax rates:', err);
    }
  }, []);

  // Fetch data for the current organizer or all events for super admin
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch events first - use getEvents() for super admin, getMyManagedEvents() for organizer
      const eventsData = canAccessAllEvents(user)
        ? await apiService.getEvents()
        : await apiService.getMyManagedEvents();
      setEvents(eventsData);
      
      // Fetch food types
      const typesData = await apiService.getFoodTypes();
      setFoodTypes(typesData);
      
      // Fetch country tax rates
      await fetchCountryTaxRates();
      
      // Fetch venues for all events
      for (const event of eventsData) {
        await fetchVenues(event.id);
      }
      
      // Fetch foods for selected event or all organizer's events
      if (selectedEvent) {
        // Fetch foods for specific event, optionally narrowed to a venue
        const foodsData = await apiService.getFoods({
          event_id: selectedEvent,
          ...(selectedVenue ? { event_venue_id: selectedVenue } : {})
        });
        setFoods(foodsData);
      } else {
        // Fetch foods for all organizer's events
        const allFoods: FoodOut[] = [];
        for (const event of eventsData) {
          const eventFoods = await apiService.getFoods({
            event_id: event.id
          });
          allFoods.push(...eventFoods);
        }
        setFoods(allFoods);
      }
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, selectedVenue, t, user, fetchVenues, fetchCountryTaxRates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (field: keyof FoodFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // The carta row shows its Product's catalog details (name, description,
  // photo, type): they're copied from the Product and are read-only here.
  const handleProductChange = (productId: number | null) => {
    const product = localProducts.find((p) => p.id === productId);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      name: product?.name ?? prev.name,
      description: product?.description ?? '',
      image_url: product?.image_url ?? '',
      type_id: product?.type_id ?? prev.type_id,
    }));
    setFormErrors(prev => ({ ...prev, product_id: '', type_id: '' }));
  };

  const handleAllergenChange = (allergen: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      allergens: checked 
        ? [...prev.allergens, allergen]
        : prev.allergens.filter(a => a !== allergen)
    }));
    // Clear error when user makes selection
    if (formErrors.allergens) {
      setFormErrors(prev => ({ ...prev, allergens: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.product_id) {
      errors.product_id = t('carta.productPicker.required');
    } else if (!formData.type_id) {
      errors.product_id = t('carta.productPicker.productNeedsType');
    }

    if (formData.price <= 0) {
      errors.price = t('errors.validation.positiveNumber');
    }

    if (formData.event_id === 0) {
      errors.event_id = t('errors.validation.required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // The first error can be far above the submit button (e.g. the product
      // picker): bring it into view so the click doesn't look like it did nothing
      setTimeout(() => {
        document.querySelector('.food-management-page__form .form-field__error')
          ?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    try {
      setIsSubmitting(true);
      
      const rate = parseFloat(taxInput || '0') || 0;
      const entered = parseFloat(priceInput || '0') || 0;
      const basePrice = isTaxIncluded 
        ? (rate > 0 ? Math.round(entered / (1 + rate / 100)) : Math.round(entered))
        : entered;

      const foodData: FoodCreate = {
        name: formData.name,
        price: basePrice,
        tax_rate: rate,
        image_url: formData.image_url || null, // the Product's photo (read-only here)
        description: formData.description || null,
        allergens: formData.allergens.length > 0 ? JSON.stringify(formData.allergens) : null,
        event_id: formData.event_id,
        event_venue_id: formData.event_venue_id,
        product_id: formData.product_id,
        type_id: formData.type_id,
        is_available: formData.is_available
      };

      if (editingFood) {
        await apiService.updateFood(editingFood.id, foodData);
        await translationsRef.current?.save();
      } else {
        await apiService.createFood(foodData);
      }

      // Reset form
      setFormData(initialFormData);
      setShowCreateForm(false);
      setEditingFood(null);
      setImageFile(null);
      
      // Refresh data
      await fetchData();
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateForm = () => {
    setEditingFood(null);
    setFormData({
      ...initialFormData,
      event_id: selectedEvent ?? 0,
      event_venue_id: selectedEvent ? selectedVenue : null,
    });
    if (selectedEvent) fetchVenues(selectedEvent);
    setFormErrors({});
    setShowCreateForm(true);
  };

  const handleEdit = (food: FoodOut) => {
    setEditingFood(food);
    setFormData({
      name: food.name,
      price: food.price,
      tax_rate: food.tax_rate,
      image_url: food.image_url || '',
      description: food.description || '',
      allergens: food.allergens ? JSON.parse(food.allergens) : [],
      event_id: food.event_id,
      event_venue_id: food.event_venue_id || null,
      product_id: food.product_id || null,
      type_id: food.type_id,
      stock_quantity: food.stock_quantity,
      max_stock: food.max_stock,
      min_stock_threshold: food.min_stock_threshold,
      is_available: food.is_available
    });
    setPriceInput(food.price ? String(food.price) : '');
    setImageFile(null);
    setShowCreateForm(true);
    setTaxInput(food.tax_rate ? String(food.tax_rate) : '');
    setIsTaxIncluded(!food.tax_rate || Number(food.tax_rate) === 0);
  };

  // Soft-deletes the selected carta rows (the catalog Product and its stock stay
  // in Productos). Each row goes through DELETE /foods/{id} so the same
  // per-row permission checks apply; failures are reported, not hidden.
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedFoodIds);
    if (ids.length === 0) return;
    if (!window.confirm(t('management.foods.bulkDelete.confirm', { count: ids.length }))) return;

    try {
      setIsBulkAssigning(true);
      const results = await Promise.allSettled(ids.map((id) => apiService.deleteFood(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(t('management.foods.bulkDelete.partial', { deleted: ids.length - failed, failed }));
      } else {
        alert(t('management.foods.bulkDelete.success', { count: ids.length }));
      }
      clearSelection();
      await fetchData();
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleToggleAvailability = async (foodId: number, currentAvailability: boolean) => {
    try {
      await apiService.updateFoodAvailability(foodId, !currentAvailability);
      await fetchData();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  };

  const handleImageChange = (file: File | null, previewUrl: string | null) => {
    setImageFile(file);
    setFormData(prev => ({ ...prev, image_url: previewUrl || '' }));
    setFormErrors(prev => ({ ...prev, image_url: '' }));
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setPriceInput('');
    setTaxInput('');
    setIsTaxIncluded(false);
    setShowCreateForm(false);
    setEditingFood(null);
    setImageFile(null);
    setFormErrors({});
  };

  // Set tax rate from global settings when event is selected or form opens
  useEffect(() => {
    if (showCreateForm && !editingFood && formData.event_id > 0 && Object.keys(countryTaxRates).length > 0) {
      const selectedEventData = events.find(e => e.id === formData.event_id);
      if (selectedEventData?.country) {
        const taxRate = countryTaxRateFor(countryTaxRates, selectedEventData.country);
        if (taxRate > 0 && formData.tax_rate !== taxRate) {
          handleInputChange('tax_rate', taxRate);
          setTaxInput(String(taxRate));
          setIsTaxIncluded(false);
        }
      }
    }
  }, [showCreateForm, formData.event_id, events, countryTaxRates, editingFood]);

  useEffect(() => {
    if (showCreateForm && !editingFood) {
      setPriceInput(formData.price > 0 ? String(formData.price) : '');
      if (formData.tax_rate > 0) {
        setTaxInput(String(formData.tax_rate));
        setIsTaxIncluded(false);
      } else {
        setTaxInput('');
        setIsTaxIncluded(true);
      }
    }
  }, [showCreateForm, editingFood, formData.price, formData.tax_rate]);

  // Keep isTaxIncluded in sync with the current tax input value
  useEffect(() => {
    if (!showCreateForm) return;
    const parsed = parseFloat(taxInput);
    const isZeroLike = taxInput === '' || isNaN(parsed) || parsed === 0;
    setIsTaxIncluded(isZeroLike);
  }, [taxInput, showCreateForm]);

  const currencyPrefix = currency.symbol || '$';

  const handlePriceInputChange = (sanitizedValue: string) => {
    setPriceInput(sanitizedValue);
    const numeric = parseFloat(sanitizedValue);
    handleInputChange('price', isNaN(numeric) ? 0 : numeric);
  };

  const handleTaxRateChange = (sanitizedValue: string) => {
    let next = sanitizedValue;
    let numeric = parseFloat(next);
    if (!isNaN(numeric) && numeric > 99) {
      next = '99';
      numeric = 99;
    }
    setTaxInput(next);
    handleInputChange('tax_rate', isNaN(numeric) ? 0 : numeric);
  };

  const handleToggleTaxIncluded = (included: boolean) => {
    // Only affect preview; do not mutate inputs
    setIsTaxIncluded(included);
  };

  // The carta template lists the event Local's products and the event's
  // bars, so it's downloaded for the event picked in the import panel.
  const handleDownloadTemplate = async () => {
    if (!bulkImportEventId) {
      setError(t('management.foods.bulkImport.selectEvent'));
      return;
    }
    try {
      const blob = await apiService.downloadFoodTemplate(bulkImportEventId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'foods_carta_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || t('management.foods.bulkImport.error'));
    }
  };

  const handleBulkImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (allowedTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setBulkImportFile(file);
        setError(null);
      } else {
        setError(t('management.foods.bulkImport.invalidFileType'));
      }
    }
  };

  const handleShowBulkImport = () => {
    setBulkImportEventId(selectedEvent); // Pre-select the filtered event
    setBulkImportResult(null);
    setShowBulkImport(true);
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      setError(t('management.foods.bulkImport.noFileSelected'));
      return;
    }

    if (!bulkImportEventId) {
      setError(t('management.foods.bulkImport.selectEvent'));
      return;
    }

    try {
      setIsBulkImporting(true);
      setError(null);
      
      const result = await apiService.bulkImportFoods(bulkImportFile, bulkImportEventId);

      // Keep the panel open with the per-row result (errors included)
      setBulkImportResult(result);
      setBulkImportFile(null);

      // Refresh data
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('management.foods.bulkImport.error'));
    } finally {
      setIsBulkImporting(false);
    }
  };

  const handleCancelBulkImport = () => {
    setBulkImportFile(null);
    setBulkImportEventId(null);
    setBulkImportResult(null);
    setShowBulkImport(false);
    setError(null);
  };

  // Bulk assign (venue / event) handlers
  const toggleFoodSelection = (foodId: number) => {
    setSelectedFoodIds(prev => {
      const next = new Set(prev);
      if (next.has(foodId)) {
        next.delete(foodId);
      } else {
        next.add(foodId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedFoodIds(new Set());
    setBulkTargetEventId(null);
    setBulkTargetVenueId(null);
  };

  useEffect(() => {
    if (bulkTargetEventId && !venues[bulkTargetEventId]) {
      fetchVenues(bulkTargetEventId);
    }
    setBulkTargetVenueId(null); // reset venue when target event changes
  }, [bulkTargetEventId, venues, fetchVenues]);

  const handleBulkAssign = async () => {
    if (selectedFoodIds.size === 0 || !bulkTargetEventId) return;

    try {
      setIsBulkAssigning(true);
      const payload: BulkAssignVenueRequest = {
        item_ids: Array.from(selectedFoodIds),
        event_id: bulkTargetEventId,
        event_venue_id: bulkTargetVenueId
      };
      const result = await apiService.bulkAssignFoodsToVenue(payload);
      alert(t('management.foods.bulkAssign.success', {
        count: result.updated_count,
        defaultValue: `${result.updated_count} platos actualizados`
      }));
      clearSelection();
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const getStockStatus = (food: FoodOut) => {
    if (food.stock_quantity <= 0) return 'out-of-stock';
    if (food.stock_quantity <= food.min_stock_threshold) return 'low-stock';
    return 'in-stock';
  };

  const getStockStatusText = (food: FoodOut) => {
    const status = getStockStatus(food);
    switch (status) {
      case 'out-of-stock':
        return t('management.foods.outOfStock');
      case 'low-stock':
        return t('management.foods.lowStock');
      default:
        return t('management.foods.inStock');
    }
  };

  const getEventName = (eventId: number) => events.find(e => e.id === eventId)?.name || '—';

  const getVenueName = (eventId: number, venueId: number | null | undefined) => {
    if (!venueId) return t('management.foods.pista', { defaultValue: 'Pista' });
    const venue = venues[eventId]?.find(v => v.id === venueId);
    if (!venue) return '—';
    return venue.venue_type_name ? `${venue.name} (${venue.venue_type_name})` : venue.name;
  };

  if (!canAccessAllEvents(user)) {
    return (
      <div className="food-management-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <h1>{t('errors.unauthorized')}</h1>
            <p>{t('management.foods.organizerOnly')}</p>
          </Card>
        </div>
      </div>
    );
  }

  // Client-side pages over the loaded rows; back to page 1 when the filters change
  const totalPages = Math.max(1, Math.ceil(foods.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageFoods = foods.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="food-management-page">
      <div className="container">
        <div className="food-management-page__header">
          <h1 className="food-management-page__title">
            {t('management.foods.title')}
          </h1>
          <p className="food-management-page__description">
            {t('management.foods.description')}
          </p>
        </div>

        {showCartaExplainer && (
          <Card variant="elevated" padding="md" className="food-management-page__explainer">
            <div className="food-management-page__explainer-header">
              <h3>{t('carta.explainer.title')}</h3>
              <Button variant="ghost" size="sm" onClick={dismissCartaExplainer}>
                {t('carta.explainer.dismiss')}
              </Button>
            </div>
            <p>{t('carta.explainer.intro')}</p>
            <Button
              variant="outline"
              size="sm"
              className="food-management-page__explainer-link"
              onClick={() => navigate('/products')}
            >
              {t('carta.explainer.goToProducts')}
            </Button>
          </Card>
        )}

        {error && (
          <Card variant="elevated" padding="md" className="food-management-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Event Filter */}
        <Card variant="elevated" padding="lg" className="food-management-page__filter">
          <div className="food-management-page__filter-content">
            <SearchableSelect<number>
              name="event_filter"
              label={t('management.foods.filterByEvent')}
              placeholder={t('management.foods.allEvents')}
              clearable
              options={events.map((event) => ({ value: event.id, label: event.name }))}
              value={selectedEvent || null}
              onChange={(value) => {
                setSelectedEvent(value);
                setSelectedVenue(null);
                setPage(1);
              }}
            />

            <SearchableSelect<number>
              name="venue_filter"
              label={t('management.foods.filterByVenue')}
              placeholder={t('management.foods.allVenues')}
              clearable
              options={(selectedEvent ? venues[selectedEvent] || [] : []).map((venue) => ({ value: venue.id, label: venue.name }))}
              value={selectedVenue || null}
              onChange={(value) => {
                setSelectedVenue(value);
                setPage(1);
              }}
              disabled={!selectedEvent}
            />

            <div className="food-management-page__action-buttons">
              <Button
                variant="primary"
                onClick={openCreateForm}
                className="food-management-page__create-btn"
              >
                {t('management.foods.addFood')}
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate(productsUrl(events.find((ev) => ev.id === selectedEvent)?.local_id, 'food'))}
              >
                {t('carta.createProduct')}
              </Button>
              
              <Button
                variant="ghost"
                onClick={handleShowBulkImport}
                className="food-management-page__bulk-import-btn"
              >
                {t('management.foods.bulkImport.uploadFile')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Bulk assign bar (visible when items are selected in the table) */}

        {/* Create/Edit Food Form */}
        {showCreateForm && (
          <Card variant="elevated" padding="lg" className="food-management-page__form">
            <h2>{editingFood ? t('management.foods.editFood') : t('management.foods.addFood')}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="food-management-page__form-grid">
                <SearchableSelect<number>
                  name="event_id"
                  label={t('management.foods.event')}
                  placeholder={t('management.foods.selectEvent')}
                  error={formErrors.event_id}
                  required
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={formData.event_id || null}
                  onChange={(value) => {
                    const eventId = value || 0;
                    handleInputChange('event_id', eventId);
                    // Reset venue selection when event changes
                    handleInputChange('event_venue_id', null);
                    // Fetch venues for the selected event
                    if (eventId > 0) {
                      fetchVenues(eventId);
                      // Set tax rate from global settings based on event's country
                      const selectedEventData = events.find(e => e.id === eventId);
                      if (selectedEventData?.country && !editingFood) {
                        const taxRate = countryTaxRateFor(countryTaxRates, selectedEventData.country);

                        handleInputChange('tax_rate', taxRate);
                        setTaxInput(String(taxRate));
                        setIsTaxIncluded(false);

                      }
                    }
                  }}
                  disabled={isSubmitting}
                />

                {editingFood && !editingFood.product_id && (
                  <div className="food-management-page__unlinked-warning form-field--full-width" role="alert">
                    <strong>{t('carta.unlinked.title')}</strong>
                    <p>{t('carta.unlinked.text')}</p>
                  </div>
                )}

                <CartaProductPicker
                  event={events.find((ev) => ev.id === formData.event_id)}
                  products={localProducts}
                  productsLoaded={localProductsLoaded}
                  value={formData.product_id}
                  onChange={handleProductChange}
                  kind="food"
                  typeName={foodTypes.find((type) => type.id === formData.type_id)?.name}
                  error={formErrors.product_id}
                  disabled={isSubmitting}
                />

                <SearchableSelect<number>
                  name="event_venue_id"
                  label={t('management.foods.venue')}
                  placeholder={t('management.foods.selectVenue')}
                  clearable
                  error={formErrors.event_venue_id}
                  options={(venues[formData.event_id] || []).map((venue) => ({ value: venue.id, label: venue.name }))}
                  value={formData.event_venue_id || null}
                  onChange={(value) => handleInputChange('event_venue_id', value)}
                  disabled={isSubmitting}
                />

                <div className="form-field form-field--full-width">
                  <label className="form-field__label">
                    {t('management.foods.allergens')}
                  </label>
                  <div className="allergen-multi-select">
                    {Object.entries(t('management.foods.allergenOptions', { returnObjects: true })).map(([key, label]) => (
                      <label key={key} className="allergen-option">
                        <input
                          type="checkbox"
                          checked={formData.allergens.includes(key)}
                          onChange={(e) => handleAllergenChange(key, e.target.checked)}
                          disabled={isSubmitting}
                        />
                        <span className="allergen-option__label">{label as string}</span>
                      </label>
                    ))}
                  </div>
                  {formErrors.allergens && (
                    <p className="form-field__error">{formErrors.allergens}</p>
                  )}
                </div>

                <FormField
                  type="price"
                  name="price"
                  label={t('management.foods.price')}
                  error={formErrors.price}
                  required
                  value={priceInput}
                  onChange={(e) => handlePriceInputChange(e.target.value)}
                  placeholder="0.00"
                  disabled={isSubmitting}
                  prefix={currencyPrefix}
                />


                  <FormField
                    type="price"
                    name="tax_rate"
                    label={t('management.foods.taxRate')}
                    error={formErrors.tax_rate}
                    value={taxInput}
                    onChange={(e) => handleTaxRateChange(e.target.value)}
                    placeholder="0.0"
                    disabled={isSubmitting || !isSuperAdmin(user)}
                    suffix="%"
                    helperText={!isSuperAdmin(user) ? t('management.foods.taxRateFromGlobalSettings') : undefined}
                  />
                

                

                {(isSuperAdmin(user) || isOrganizer(user)) && (
                  <div className="form-field form-field--full-width">
                    <label className="form-field__label">
                      {t('management.foods.taxRateIncludedQuestion')}
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '50%' }}>
                        <input
                          className="food-management-page__tax-included-radio"
                          type="radio"
                          name="tax_included"
                          checked={isTaxIncluded}
                          onChange={() => handleToggleTaxIncluded(true)}
                          disabled={isSubmitting}
                        />
                        <span>{t('management.foods.taxRateIncluded')}</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '50%' }}>
                        <input
                          className="food-management-page__tax-included-radio"
                          type="radio"
                          name="tax_included"
                          checked={!isTaxIncluded}
                          onChange={() => handleToggleTaxIncluded(false)}
                          disabled={isSubmitting}
                        />
                        <span>{t('management.foods.taxRateNotIncluded')}</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Auto-calculation preview */}
                <div className="form-field form-field--full-width food-management-page__price-preview">
                  {(() => {
                    const rate = parseFloat(taxInput || '0') || 0;
                    if (isTaxIncluded) {
                      const finalPrice = parseFloat(priceInput || '0') || 0;
                      const base = rate > 0 ? finalPrice / (1 + rate / 100) : finalPrice;
                      const taxAmount = finalPrice - base;
                      return (
                        <>
                          <div className="price-preview__row">
                            <span className="price-preview__label">{t('management.common.basePrice', 'Precio base')}:</span>
                            <span className="price-preview__value">{format(base)}</span>
                          </div>
                          {rate > 0 && (
                            <div className="price-preview__row">
                              <span className="price-preview__label">{t('management.common.tax', 'IVA')} ({rate.toFixed(1)}%):</span>
                              <span className="price-preview__value">{format(taxAmount)}</span>
                            </div>
                          )}
                          <div className="price-preview__row price-preview__row--total">
                            <span className="price-preview__label">{t('management.common.finalPrice', 'Precio final')}:</span>
                            <span className="price-preview__value">{format(finalPrice)}</span>
                          </div>
                        </>
                      );
                    }
                    const base = parseFloat(priceInput || '0') || 0;
                    const taxAmount = base * (rate / 100);
                    const finalPrice = base + taxAmount;
                    return (
                      <>
                        <div className="price-preview__row">
                          <span className="price-preview__label">{t('management.common.basePrice', 'Precio base')}:</span>
                          <span className="price-preview__value">{format(base)}</span>
                        </div>
                        {rate > 0 && (
                          <div className="price-preview__row">
                            <span className="price-preview__label">{t('management.common.tax', 'IVA')} ({rate.toFixed(1)}%):</span>
                            <span className="price-preview__value">{format(taxAmount)}</span>
                          </div>
                        )}
                        <div className="price-preview__row price-preview__row--total">
                          <span className="price-preview__label">{t('management.common.finalPrice', 'Precio final')}:</span>
                          <span className="price-preview__value">{format(finalPrice)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="food-management-page__product-stock-note form-field--full-width">
                  <p>{t('carta.stockManagedInProducts')}</p>
                  {formData.product_id && events.find((ev) => ev.id === formData.event_id)?.local_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(warehouseProductUrl(events.find((ev) => ev.id === formData.event_id)?.local_id!, formData.product_id!))}
                    >
                      {t('carta.viewStock')}
                    </Button>
                  )}
                </div>

                <FormField
                  type="checkbox"
                  name="is_available"
                  label={t('management.foods.available')}
                  error={formErrors.is_available}
                  checked={formData.is_available}
                  onChange={(e) => handleInputChange('is_available', (e.target as HTMLInputElement).checked)}
                  disabled={isSubmitting}
                />
              </div>

              
              
                
              
              {editingFood && (
                <TranslationFields
                  ref={translationsRef}
                  entityType="food"
                  entityId={editingFood.id}
                  disabled={isSubmitting}
                />
              )}

              <div className="food-management-page__form-actions">
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : (editingFood ? t('common.update') : t('common.create'))}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Bulk Import Form */}
        {showBulkImport && (
          <Card variant="elevated" padding="lg" className="food-management-page__bulk-import-form">
            <h2>{t('management.foods.bulkImport.title')}</h2>
            <p className="food-management-page__bulk-import-description">
              {t('management.foods.bulkImport.description')}
            </p>
            
            <div className="food-management-page__bulk-import-content">
              <div className="food-management-page__file-upload-section">
                <SearchableSelect<number>
                  name="bulk_import_event"
                  label={t('management.foods.bulkImport.selectEvent')}
                  placeholder={t('management.foods.bulkImport.selectEventPlaceholder')}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={bulkImportEventId || null}
                  onChange={(value) => {
                    setBulkImportEventId(value);
                    setBulkImportResult(null);
                  }}
                  disabled={isBulkImporting}
                />

                <label className="form-field__label">
                  {t('management.foods.bulkImport.selectFile')}
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBulkImportFileChange}
                  className="food-management-page__file-input"
                  disabled={isBulkImporting}
                />
                {bulkImportFile && (
                  <p className="food-management-page__file-selected">
                    {t('management.foods.bulkImport.fileSelected', { fileName: bulkImportFile.name })}
                  </p>
                )}
              </div>

              <div className="food-management-page__bulk-import-actions">
                <Button
                  variant="ghost"
                  onClick={handleDownloadTemplate}
                  className="food-management-page__download-btn"
                  disabled={!bulkImportEventId}
                >
                  {t('management.foods.bulkImport.downloadTemplate')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleBulkImport}
                  disabled={!bulkImportFile || !bulkImportEventId || isBulkImporting}
                >
                  {isBulkImporting ? t('management.foods.bulkImport.importing') : t('management.foods.bulkImport.import')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelBulkImport}
                  disabled={isBulkImporting}
                >
                  {bulkImportResult ? t('common.close') : t('common.cancel')}
                </Button>
              </div>
            </div>

            {bulkImportResult && (
              <BulkImportResult result={bulkImportResult} onDismiss={() => setBulkImportResult(null)} />
            )}
          </Card>
        )}

        {/* Foods List */}
        <div className="food-management-page__foods">
          {/* Always shown, like the filters, so selecting rows doesn't push the list down */}
            <Card variant="elevated" padding="md" className={`food-management-page__bulk-assign-bar ${selectedFoodIds.size === 0 ? 'food-management-page__bulk-assign-bar--idle' : ''}`}>
              <div className="food-management-page__bulk-assign-content">
                <span className="food-management-page__bulk-assign-count">
                  {selectedFoodIds.size === 0
                    ? t('management.foods.bulkAssign.hint')
                    : t('management.foods.bulkAssign.selectedCount', {
                        count: selectedFoodIds.size,
                        defaultValue: `${selectedFoodIds.size} seleccionados`
                      })}
                </span>

                <SearchableSelect<number>
                  name="bulk_target_event"
                  label={t('management.foods.bulkAssign.targetEvent', { defaultValue: 'Evento destino' })}
                  placeholder={t('management.foods.bulkAssign.selectEvent', { defaultValue: 'Elige evento' })}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={bulkTargetEventId || null}
                  onChange={(value) => setBulkTargetEventId(value)}
                  disabled={isBulkAssigning || selectedFoodIds.size === 0}
                />

                <SearchableSelect<number>
                  name="bulk_target_venue"
                  label={t('management.foods.bulkAssign.targetVenue', { defaultValue: 'Barra destino' })}
                  placeholder={t('management.foods.bulkAssign.noVenue', { defaultValue: '— Sin barra (Pista) —' })}
                  clearable
                  options={(bulkTargetEventId ? venues[bulkTargetEventId] || [] : []).map((venue) => ({
                    value: venue.id,
                    label: `${venue.name}${venue.venue_type_name ? ` (${venue.venue_type_name})` : ''}`,
                  }))}
                  value={bulkTargetVenueId || null}
                  onChange={(value) => setBulkTargetVenueId(value)}
                  disabled={isBulkAssigning || selectedFoodIds.size === 0 || !bulkTargetEventId}
                />

                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkAssign}
                  disabled={isBulkAssigning || selectedFoodIds.size === 0 || !bulkTargetEventId}
                >
                  {isBulkAssigning
                    ? t('common.loading')
                    : t('management.foods.bulkAssign.assign', { defaultValue: 'Asignar' })}
                </Button>
                <Button
                  variant="error"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isBulkAssigning || selectedFoodIds.size === 0}
                >
                  {t('management.foods.bulkDelete.button')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                  disabled={isBulkAssigning || selectedFoodIds.size === 0}
                >
                  {t('management.foods.bulkAssign.clear')}
                </Button>
              </div>
            </Card>
          {loading ? (
            <Card variant="elevated" padding="lg">
              <p>{t('common.loading')}</p>
            </Card>
          ) : foods.length === 0 ? (
            <Card variant="elevated" padding="lg">
              <p>{t('management.foods.noFoods')}</p>
            </Card>
          ) : (
            <Card variant="elevated" padding="none" className="food-management-page__foods-table-card">
              <div className="food-management-page__table-wrapper">
                <table className="food-management-page__table">
                  <thead>
                    <tr>
                      <th className="food-management-page__table-checkbox-col">
                        <input
                          type="checkbox"
                          checked={pageFoods.length > 0 && pageFoods.every((f) => selectedFoodIds.has(f.id))}
                          onChange={(e) => {
                            // Selects or clears the rows of the current page only
                            const next = new Set(selectedFoodIds);
                            pageFoods.forEach((f) => (e.target.checked ? next.add(f.id) : next.delete(f.id)));
                            setSelectedFoodIds(next);
                          }}
                        />
                      </th>
                      <th>{t('management.foods.name')}</th>
                      <th>{t('carta.table.product')}</th>
                      <th>{t('management.foods.type')}</th>
                      <th>{t('management.foods.event')}</th>
                      <th>{t('management.foods.venue', { defaultValue: 'Barra' })}</th>
                      <th>{t('management.foods.price')}</th>
                      <th>{t('management.foods.taxRate')}</th>
                      <th>{t('management.foods.currentStock')}</th>
                      <th>{t('management.foods.available')}</th>
                      <th className="food-management-page__table-actions-col">{t('common.actions', { defaultValue: 'Acciones' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageFoods.map((food) => (
                      <tr
                        key={food.id}
                        className={selectedFoodIds.has(food.id) ? 'food-management-page__table-row--selected' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedFoodIds.has(food.id)}
                            onChange={() => toggleFoodSelection(food.id)}
                          />
                        </td>
                        <td>
                          <div className="food-management-page__table-name-cell">
                            {food.image_url && (
                              <img
                                src={food.image_url}
                                alt={food.name}
                                className="food-management-page__table-thumb"
                              />
                            )}
                            <div>
                              <div className="food-management-page__table-name">{food.name}</div>
                              <span className={`food-management-page__stock-badge ${getStockStatus(food)}`}>
                                {getStockStatusText(food)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {food.product_id ? (
                            <span className="food-management-page__product-badge food-management-page__product-badge--linked">
                              {t('carta.table.linked')}
                            </span>
                          ) : (
                            <span
                              className="food-management-page__product-badge food-management-page__product-badge--unlinked"
                              title={t('carta.unlinked.text')}
                            >
                              {t('carta.table.unlinked')}
                            </span>
                          )}
                        </td>
                        <td>{foodTypes.find(t => t.id === food.type_id)?.name}</td>
                        <td>{events.find((event) => event.id === food.event_id)?.name}</td>
                        <td>{getVenueName(food.event_id, food.event_venue_id)}</td>
                        <td>{format(food.price)}</td>
                        <td>{food.tax_rate}%</td>
<td>
                          {food.product_id && events.find((ev) => ev.id === food.event_id)?.local_id ? (
                            <button
                              type="button"
                              className="carta-stock-link"
                              title={t('carta.viewStock')}
                              onClick={() => navigate(warehouseProductUrl(events.find((ev) => ev.id === food.event_id)?.local_id!, food.product_id!))}
                            >
                              {food.stock_quantity} ↗
                            </button>
                          ) : (
                            food.stock_quantity
                          )}
                        </td>
                        <td>
                          {food.is_available ? 'Y' : 'N'}
                        </td>
                        <td>
                          <div className="food-management-page__table-actions-cell">
                            <Button variant="primary" size="sm" onClick={() => handleEdit(food)}>
                              {t('common.edit')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                className="food-management-page__pagination"
                page={currentPage}
                pageSize={PAGE_SIZE}
                totalItems={foods.length}
                onPageChange={setPage}
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoodManagement;